import { AnimatePresence, motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBook,
    FaCheckCircle,
    FaCube,
    FaExternalLinkAlt,
    FaFingerprint,
    FaGithub,
    FaMicrochip,
    FaShieldAlt,
    FaTerminal
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiChevronRight,
    TiCloudStorageOutline,
    TiCodeOutline,
    TiLockClosedOutline,
    TiRadarOutline
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Developers = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [activeTab, setActiveTab] = useState("Installation");

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 90%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const protocols = [
    {
      title: "The Modish Engine",
      role: "Software Infrastructure",
      desc: "High-performance SDK for building sovereign interactive systems and distributed UI frameworks.",
      icon: <TiCodeOutline />,
      tech: ["React / Three.js", "Distributed State", "Direct Interactivity"]
    },
    {
      title: "Zero-Block L1",
      role: "Consensus & Ledger",
      desc: "Sovereign consensus mechanism engineered for institutional-grade reliability and permanent truss validation.",
      icon: <FaShieldAlt />,
      tech: ["Rust Core", "Proof of Sovereignty", "High Liquidity"]
    },
    {
      title: "Machu Hardware Bridge",
      role: "Digital Twin API",
      desc: "IoT and hardware synchronization layer bridging architectural assets with their digital sovereign twins.",
      icon: <FaMicrochip />,
      tech: ["Digital Twin API", "Hardware IoT", "Physical-Digital Symbiosis"]
    },
    {
      title: "ReguSync SDK",
      role: "Compliance-as-Code",
      desc: "Institutional middleware for programmatic compliance and global regulatory synchronization.",
      icon: <FaCheckCircle />,
      tech: ["Programmatic Ops", "ESG Syncing", "Regulatory Logic"]
    }
  ];

  const repos = [
    { name: "zero-monorepo", stats: "4.2k Stars • 150 Contributors", tag: "Main Core" },
    { name: "machu-iot-bridge", stats: "1.8k Stars • 45 Contributors", tag: "Hardware" },
    { name: "modish-core-engine", stats: "2.9k Stars • 82 Contributors", tag: "Software" },
    { name: "regusync-logic", stats: "1.2k Stars • 30 Contributors", tag: "Compliance" }
  ];

  const terminalContent = {
    "Installation": [
      { cmd: "npm install @zero-blockchain/core", output: "initializing sovereign-logic installation..." },
      { cmd: "zero-init --brand modish0161", output: "configuring modish-engine environment..." },
      { cmd: "zero-deploy", output: "deploying sovereign node to Zero-Block L1..." }
    ],
    "Consensus": [
      { cmd: "zero-node status", output: "state: sovereign | health: 99.9%" },
      { cmd: "zero-consensus --validate", output: "verifying permanent trust protocols..." },
      { cmd: "zero-sync --global", output: "syndicating state to 12-brand alliance..." }
    ],
    "Bridge API": [
      { cmd: "machu-bridge connect --id unit-01", output: "Establishing hardware-software handshake..." },
      { cmd: "machu-bridge sync --twin", output: "Synchronizing digital twin metadata..." },
      { cmd: "machu-bridge monitor", output: "Active monitoring for unit-01: OK" }
    ]
  };

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/developers-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="developersHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={60}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Sovereign Logic"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Engineering the Foundations of Permanent Trust
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Core Protocols Bento */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-48">
          {protocols.map((protocol, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group p-10 rounded-[3rem] bg-white/5 border border-white/10 hover:border-indigo-500/30 transition-all hover:bg-white/10 flex flex-col h-full overflow-hidden relative"
              ref={(el) => (sectionRefs.current[index] = el)}
            >
              <div className="absolute -top-10 -right-10 text-[180px] text-white/[0.02] group-hover:text-indigo-500/[0.05] transition-colors pointer-events-none">
                {protocol.icon}
              </div>
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-2xl text-indigo-400 mb-8 group-hover:scale-110 transition-transform">
                {protocol.icon}
              </div>
              <h3 className="text-lg font-bold mb-4 text-white uppercase tracking-wider">{protocol.title}</h3>
              <div className="text-[9px] font-bold text-indigo-500/60 uppercase tracking-[0.3em] mb-6">{protocol.role}</div>
              <p className="text-gray-300 text-xs leading-relaxed mb-10 flex-grow">{protocol.desc}</p>
              
              <div className="flex flex-wrap gap-2 pt-6 border-t border-white/5">
                {protocol.tech.map((t, idx) => (
                  <span key={idx} className="text-[7px] font-bold text-gray-500 uppercase tracking-widest">{t}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Interactive Documentation Terminal */}
        <div className="mb-48 p-1 rounded-[4rem] bg-gradient-to-br from-indigo-500/20 to-transparent border border-white/10 overflow-hidden shadow-2xl">
           <div className="bg-black/80 backdrop-blur-3xl p-12 md:p-20 relative">
              <div className="flex flex-col lg:flex-row gap-20">
                 <div className="w-full lg:w-2/5">
                    <div className="flex items-center gap-3 text-indigo-400 font-bold uppercase tracking-[0.4em] text-[10px] mb-8">
                       <FaTerminal className="text-xl" />
                       Technical Terminal
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-10">
                       Initialize Your <span className="text-indigo-500 font-zentry italic uppercase text-6xl block mt-2">Build Protocol</span>
                    </h2>
                    <p className="text-gray-300 text-lg leading-relaxed mb-12 italic">
                      Seamlessly integrate with the 12-brand alliance technical ecosystem using our sovereign command-line interface.
                    </p>
                    
                    <div className="space-y-4">
                       {Object.keys(terminalContent).map((tab) => (
                         <button
                           key={tab}
                           onClick={() => setActiveTab(tab)}
                           className={`w-full text-left px-8 py-5 rounded-3xl border transition-all flex items-center justify-between group ${
                             activeTab === tab 
                               ? "bg-indigo-600 border-indigo-500 text-black font-bold" 
                               : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
                           }`}
                         >
                            <span className="text-[10px] uppercase tracking-[0.2em]">{tab} Protocol</span>
                            <TiChevronRight className={`text-xl transition-transform ${activeTab === tab ? "translate-x-1" : "group-hover:translate-x-1"}`} />
                         </button>
                       ))}
                    </div>
                 </div>

                 <div className="w-full lg:w-3/5 bg-slate-950 rounded-[2.5rem] border border-white/10 p-8 md:p-12 font-mono text-[13px] shadow-inner relative">
                    <div className="flex gap-2 mb-10">
                       <div className="w-3 h-3 rounded-full bg-red-500/50" />
                       <div className="w-3 h-3 rounded-full bg-orange-500/50" />
                       <div className="w-3 h-3 rounded-full bg-green-500/50" />
                    </div>
                    
                    <AnimatePresence mode="wait">
                       <motion.div
                         key={activeTab}
                         initial={{ opacity: 0, y: 10 }}
                         animate={{ opacity: 1, y: 0 }}
                         exit={{ opacity: 0, y: -10 }}
                         transition={{ duration: 0.3 }}
                         className="space-y-8"
                       >
                          {terminalContent[activeTab].map((line, idx) => (
                            <div key={idx}>
                               <div className="flex items-center gap-3 text-white/40 mb-2">
                                  <span className="text-indigo-500">➜</span>
                                  <span className="text-gray-500">~ /zero-engineering</span>
                               </div>
                               <div className="text-indigo-400 font-bold mb-3">{line.cmd}</div>
                               <div className="text-white/60 pl-6 border-l border-white/5">{line.output}</div>
                            </div>
                          ))}
                          <div className="flex items-center gap-3 text-white/40 pt-4">
                             <span className="text-indigo-500 animate-pulse">▋</span>
                          </div>
                       </motion.div>
                    </AnimatePresence>
                 </div>
              </div>
           </div>
        </div>

        {/* GitHub Ecosystem Hub */}
        <div className="mb-48">
           <div className="flex items-center justify-center gap-3 text-indigo-400 font-bold uppercase tracking-[0.4em] text-[10px] mb-8">
              <FaGithub className="text-2xl" />
              Sovereign Repositories
           </div>
           <h2 className="text-4xl md:text-6xl font-bold text-center mb-24">Ecosystem <span className="font-zentry italic text-indigo-400 uppercase text-7xl block mt-2">Open Source</span></h2>
           
           <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {repos.map((repo, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -10 }}
                  className="p-12 rounded-[4rem] bg-white/5 border border-white/10 hover:border-indigo-500/30 transition-all flex flex-col md:flex-row items-center justify-between group"
                >
                   <div className="flex items-center gap-8 mb-8 md:mb-0">
                      <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center text-4xl group-hover:text-indigo-400 transition-colors">
                         <TiCloudStorageOutline />
                      </div>
                      <div>
                         <h3 className="text-2xl font-bold text-white group-hover:text-indigo-400 transition-colors">{repo.name}</h3>
                         <p className="text-xs text-gray-500 font-bold uppercase mt-2">{repo.stats}</p>
                      </div>
                   </div>
                   <div className="flex items-center gap-4">
                      <span className="text-[8px] font-bold text-gray-600 border border-white/5 px-4 py-2 rounded-full uppercase tracking-widest">{repo.tag}</span>
                      <GlassButton
                        className="w-12 h-12 flex items-center justify-center p-0 rounded-full"
                        variant="indigo"
                        size="sm"
                        ariaLabel="External Link"
                      >
                         <FaExternalLinkAlt />
                      </GlassButton>
                   </div>
                </motion.div>
              ))}
           </div>
        </div>

        {/* Final Build CTA */}
        <motion.div
           initial={{ opacity: 0, scale: 0.95 }}
           whileInView={{ opacity: 1, scale: 1 }}
           viewport={{ once: true }}
           className="relative text-center py-48"
        >
           <div className="absolute inset-0 bg-indigo-500/5 rounded-[100%] blur-[150px]" />
           <div className="relative z-10 max-w-4xl mx-auto px-6">
              <div className="w-24 h-24 rounded-[2.5rem] bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-5xl text-indigo-400 mx-auto mb-16 shadow-[0_0_50px_rgba(79,70,229,0.2)]">
                <FaCube />
              </div>
              <h2 className="text-5xl md:text-7xl font-bold mb-12 leading-tight">
                 Initialize <span className="font-zentry italic text-indigo-400 text-8xl uppercase tracking-tighter block mt-4">The Protocol</span>
              </h2>
              <p className="text-gray-300 text-xl leading-relaxed mb-20 max-w-2xl mx-auto italic border-r-2 border-indigo-500/30 pr-10">
                The 12-brand alliance technical foundation is open for sovereign builders. Start engineering the foundations of permanent trust today.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                 <GlassButton
                   className="px-16 py-8 text-xl"
                   variant="indigo"
                   size="lg"
                 >
                   <div className="flex items-center gap-4">
                     <FaBook /> Technical Whitepaper
                   </div>
                 </GlassButton>
                 <GlassButton
                   className="px-16 py-8 text-xl"
                   variant="gray"
                   size="lg"
                 >
                   <div className="flex items-center gap-4">
                     Access GitHub <TiArrowRight className="text-2xl text-indigo-400" />
                   </div>
                 </GlassButton>
              </div>

              <div className="mt-32 flex flex-wrap justify-center gap-16 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaFingerprint /> BIOMETRIC AUTH</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><TiLockClosedOutline size={18} /> L1 SECURITY</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><TiRadarOutline size={18} /> STATE VALIDATION</div>
              </div>
           </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-indigo-500/30 rounded-full shadow-lg shadow-indigo-500/10 cursor-pointer hover:bg-black/60 hover:border-indigo-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-indigo-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Developers;
